import { useEffect, useEffectEvent, useRef, useState } from "react";

type UseIdleProps = {
  idleTime: number;
  onIdle?: () => Promise<void>;
  disable?: boolean;
};

export const useIdle = ({ idleTime, onIdle, disable }: UseIdleProps) => {
  const [isIdle, setIsIdle] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDisabled = idleTime <= 0 || disable;

  // useEffectEvent ensures onIdle always calls the latest version
  // without causing the effect to re-run when onIdle changes
  const stableOnIdle = useEffectEvent(() => {
    onIdle?.().catch(console.error);
  });

  useEffect(() => {
    if (isDisabled) return;

    const handleActivity = () => {
      setIsIdle(false);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true);
        stableOnIdle();
      }, idleTime);
    };

    // Start the idle timer immediately
    handleActivity();

    const events = [
      "click",
      "mousemove",
      "keydown",
      "focusin",
      "touchstart",
      "touchmove",
      "pointerdown",
      "wheel",
    ] as const;

    events.forEach(event =>
      window.addEventListener(event, handleActivity, { passive: true }),
    );

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach(event =>
        window.removeEventListener(event, handleActivity),
      );
    };
  }, [idleTime, isDisabled]);

  return isIdle;
};
